import { useCallback, useEffect, useRef, useState } from 'react';
import debounce from 'lodash/debounce';

import type { AutosaveState } from '../components/AutosaveStatus';

interface UseAutosaveOptions<T> {
  onSave: (data: T) => Promise<void>;
  debounceMs?: number;
}

interface UseAutosaveReturn<T> {
  status: AutosaveState;
  isDirty: boolean;
  lastSavedAt: Date | null;
  triggerSave: (data: T) => void;
  retry: () => void;
}

/**
 * Custom hook for managing autosave functionality with debouncing
 * @param onSave - Async function to save the data
 * @param debounceMs - Milliseconds to debounce (default: 800ms)
 * @returns Autosave state and control functions
 */
export function useAutosave<T>({
  onSave,
  debounceMs = 800,
}: UseAutosaveOptions<T>): UseAutosaveReturn<T> {
  const [status, setStatus] = useState<AutosaveState>('idle');
  const [isDirty, setIsDirty] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const lastDataRef = useRef<T>();
  const isMountedRef = useRef(true);

  // Perform the actual save operation
  const performSave = useCallback(
    async (data: T) => {
      if (!isMountedRef.current) return;

      setStatus('saving');
      try {
        await onSave(data);

        if (isMountedRef.current) {
          setStatus('idle');
          setIsDirty(false);
          setLastSavedAt(new Date());
          lastDataRef.current = data;
        }
      } catch (error) {
        if (isMountedRef.current) {
          setStatus('error');
          console.error('Autosave failed:', error);
        }
      }
    },
    [onSave],
  );

  // Create debounced save function
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((data: T) => {
      performSave(data);
    }, debounceMs),
    [performSave, debounceMs],
  );

  // Trigger a save (will be debounced)
  const triggerSave = useCallback(
    (data: T) => {
      setIsDirty(true);
      lastDataRef.current = data;
      debouncedSave(data);
    },
    [debouncedSave],
  );

  // Retry the last failed save
  const retry = useCallback(() => {
    if (lastDataRef.current) {
      performSave(lastDataRef.current);
    }
  }, [performSave]);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      debouncedSave.cancel();
    };
  }, [debouncedSave]);

  return {
    status,
    isDirty,
    lastSavedAt,
    triggerSave,
    retry,
  };
}
